import { MapPin, Phone, Mail, Clock } from "lucide-react";
import Reveal, { Overline } from "@/components/Reveal";
import { DEALERSHIP } from "@/data/site";
import { scrollToSection } from "@/lib/scroll";

export default function Locations() {
  return (
    <section id="visit" data-testid="locations-section" className="relative bg-[#05141F] py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Overline>Find Us</Overline>
          <h2 className="mt-4 font-display font-extrabold tracking-tight leading-[1.02] text-3xl sm:text-5xl text-white">
            On the Highway. <span className="text-outline">Always Open.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 gap-6">
          <Reveal>
            <div data-testid="location-card-showroom" className="h-full bg-[#0D1822] border border-white/10 hover:border-[#FFFFFF]/40 transition-colors duration-500 clip-card p-8 flex flex-col">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFFFFF]">Flagship Showroom</p>
              <h3 className="mt-3 font-display font-bold text-2xl text-white tracking-tight">{DEALERSHIP.name}</h3>
              <p className="text-sm text-slate-500 mt-1">{DEALERSHIP.legal}</p>

              <div className="mt-6 space-y-4 text-sm text-slate-400 flex-1">
                <p className="flex gap-3"><MapPin size={16} className="text-[#FFFFFF] shrink-0 mt-0.5" /> {DEALERSHIP.address}</p>
                <p className="flex gap-3 items-center">
                  <Phone size={16} className="text-[#FFFFFF] shrink-0" />
                  <a data-testid="location-phone" href={`tel:${DEALERSHIP.phoneRaw}`} className="hover:text-white transition-colors">{DEALERSHIP.phone}</a>
                </p>
                <p className="flex gap-3 items-center">
                  <Mail size={16} className="text-[#FFFFFF] shrink-0" />
                  <a data-testid="location-email" href={`mailto:${DEALERSHIP.email}`} className="hover:text-white transition-colors">{DEALERSHIP.email}</a>
                </p>
                <p className="flex gap-3 items-center"><Clock size={16} className="text-[#FFFFFF] shrink-0" /> {DEALERSHIP.hours}</p>
              </div>

              <div className="mt-7 flex flex-wrap gap-2">
                {DEALERSHIP.services.map((s) => (
                  <span key={s} className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-400 border border-white/10 px-3 py-1.5">
                    {s}
                  </span>
                ))}
              </div>

              <a
                data-testid="location-call-button"
                href={`tel:${DEALERSHIP.phoneRaw}`}
                className="mt-8 inline-flex items-center justify-center gap-2 bg-[#FFFFFF] hover:bg-[#D4D4D4] text-black text-xs font-semibold uppercase tracking-[0.18em] py-3.5 transition-colors duration-300"
              >
                <Phone size={14} /> Call Now
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div data-testid="location-card-exchange" className="h-full bg-[#0D1822] border border-white/10 hover:border-[#FFFFFF]/40 transition-colors duration-500 clip-card p-8 flex flex-col">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFFFFF]">Exclusive Offers</p>
              <h3 className="mt-3 font-display font-bold text-2xl text-white tracking-tight">Hassle-Free Exchange</h3>

              <ul className="mt-6 space-y-4 text-sm text-slate-400 flex-1">
                {[
                  "New car with old EMI — packaged exchange deal",
                  "Car evaluation at your convenience",
                  "Best price for your old car",
                  "Hassle-free transfer of ownership",
                  "Complete assurance your car goes into the right hands",
                ].map((offer, i) => (
                  <li key={offer} data-testid={`exchange-offer-${i}`} className="flex gap-3">
                    <span className="font-mono text-[10px] text-[#FFFFFF] mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                    {offer}
                  </li>
                ))}
              </ul>

              <button
                data-testid="exchange-book-test-drive-button"
                onClick={() => scrollToSection("#test-drive")}
                className="mt-8 inline-flex items-center justify-center gap-2 border border-white/20 hover:border-[#FFFFFF] text-white text-xs font-semibold uppercase tracking-[0.18em] py-3.5 transition-colors duration-300"
              >
                Book a Test Drive
              </button>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div data-testid="showroom-map-wrapper" className="mt-6 border border-white/10 clip-card overflow-hidden">
            <iframe
              data-testid="showroom-map"
              title="Sanjib Kia Showroom — NH-20, Panikoili, Jajpur"
              src="https://maps.google.com/maps?q=Sanjib%20Kia%20NH-20%20Panikoili%20Jajpur%20Odisha%20755019&t=&z=15&ie=UTF8&iwloc=&output=embed"
              className="w-full h-[380px] map-dark"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
